import { useState } from 'react';

import { notifications } from '@mantine/notifications';
import useSWR from 'swr';

import { getUser } from '@/features/auth/api/getUser';
import { useAuthorizeRouter } from '@/features/auth/hooks/useAuthorizeRouter';
import { getSite } from '@/features/saveSite/api/getSite';
import { getUserSession } from '@/lib/auth/auth';
import { OutputErrorLog } from '@/lib/error';
import { accessS3Client } from '@/lib/s3/accessS3Client';
import { SWR_KEYS } from '@/utils/constant';

const PROCESSING_SHAPE_REPORT_FILE_NAME = 'processing-shape-report.xlsx';

export const useDownloadProcessingShapeReport = () => {
  const router = useAuthorizeRouter();
  const { siteId } = router.query;

  const { data: site } = useSWR([SWR_KEYS.SITE, siteId], () =>
    siteId ? getSite({ siteId }) : null
  );
  const { data: user } = useSWR(SWR_KEYS.USER, () => getUser());

  const [isLoading, setIsLoading] = useState(false);

  const downloadProcessingShapeReport = async () => {
    try {
      setIsLoading(true);

      //ユーザー情報とサイト情報が取得できていない場合はエラー
      if (!user || !site) {
        throw new Error('user or site is undefined');
      }

      const userSession = await getUserSession({
        isSubstitute: user.isSubstitute,
        systemRoleTypeId: user.systemRoleTypeId,
        companyCode: user.companyCode,
      });
      if (!userSession) {
        throw new Error('userSession is undefined');
      }
      const { idToken, customRoleArn } = userSession;

      // 公開状態によってS3のパスを変更
      const topPath = site.isOpen ? 'public' : 'private';
      // S3のKeyを作成
      const Key = `${topPath}/${user.companyCode}/${site.id}-${site.openRandomString}/processing-shape-report/${PROCESSING_SHAPE_REPORT_FILE_NAME}`;
      // バケット取得
      const body = await accessS3Client({
        idToken,
        cognitoRoleARN: customRoleArn,
        Key,
      });

      if (!body) {
        throw new Error('Failed to access S3');
      }

      // ダウンロード
      const blob = await new Response(body).blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = PROCESSING_SHAPE_REPORT_FILE_NAME;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      OutputErrorLog('downloadProcesingShape', e);
      notifications.show({
        title: 'エラー',
        message: 'ファイルのダウンロードに失敗しました',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    downloadProcessingShapeReport,
    isLoading,
  };
};
